"use client";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../shadcn/ui/tooltip";
import { UserEvents, trackEvent } from "@punchup/analytics";
import { useEffect, useRef, useState } from "react";

import { CopyIcon } from "lucide-react";
import { cn } from "@punchup/utils/cn";
import { toast } from "../../shadcn/ui/use-toast";

export default function MetadataBadge({
  text,
  className,
  lineClamp = 2,
}: {
  text: string;
  className?: string;
  lineClamp?: number;
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);
  const isPresaleCode = text.toLowerCase().slice(0, 5) === "code:";

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    if (isMobile && textRef.current) {
      const el = textRef.current as HTMLElement;
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    }
  }, [text, isMobile]);

  let cleanedCode = "";
  if (isPresaleCode) {
    cleanedCode = text.slice(4, text.length).replace(":", "").trim();
  }

  const copyToClipboard = (presaleCode: string) => {
    trackEvent(UserEvents.COPIED_PRESALE_CODE_TO_CLIPBOARD);
    navigator.clipboard.writeText(presaleCode).then(() => {
      toast({
        title: "Copied Presale Code to Clipboard!",
        variant: "success",
      });
    });
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  if (isPresaleCode) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className={cn(
                "h-[40px] max-w-full text-sm font-medium leading-normal rounded-md text-white px-2 py-1 bg-slate-gray-600 hover:bg-slate-gray-400 duration-200 hover:ease-in-out flex items-center justify-center gap-x-1",
                className
              )}
              onClick={() => copyToClipboard(cleanedCode)}
            >
              <CopyIcon width={15} height={15} strokeWidth={2.5} />
              <span className="truncate">{text}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent
            sideOffset={5}
            className="max-w-xs bg-primary-blue text-white pointer-events-none"
          >
            Click to Copy the Presale Code
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (isMobile) {
    return (
      <div className={cn("text-md text-slate-600", className)}>
        {isExpanded ? (
          <div className="break-words">
            <p className="whitespace-pre-wrap break-words">{text}</p>
            <button
              className="text-primary-blue font-medium mt-1"
              onClick={toggleExpand}
            >
              Show less
            </button>
          </div>
        ) : (
          <div className="relative">
            <p
              ref={textRef}
              className={cn(
                "overflow-hidden text-ellipsis",
                lineClamp === 1 ? "line-clamp-1" : "line-clamp-2"
              )}
            >
              {text}
            </p>
            {isOverflowing && (
              <button
                className="text-primary-blue font-medium inline-block mt-1"
                onClick={toggleExpand}
              >
                Read more
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <p className={cn("text-md text-slate-600", className)}>
            <span
              className={cn(
                "overflow-hidden text-ellipsis",
                lineClamp === 1 ? "line-clamp-1" : "line-clamp-2"
              )}
            >
              {text}
            </span>
          </p>
        </TooltipTrigger>
        <TooltipContent
          sideOffset={5}
          className="max-w-xs bg-primary-blue text-white rounded-lg"
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
